(() => {
  if (window.awMotionGallery) return;
  const instances = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const smooth = value => value * value * (3 - 2 * value);
  const isPaused = () => reduced.matches || document.documentElement.classList.contains('aw-motion-off');

  function exhibition(root) {
    const controller = new AbortController();
    const signal = controller.signal;
    const pieces = [...root.querySelectorAll('[data-aw-exhibit-piece]')];
    const buttons = [...root.querySelectorAll('[data-aw-exhibit-select]')];
    const count = root.querySelector('[data-aw-exhibit-count]');
    const stage = root.querySelector('.aw-exhibition-stage');
    let frame = 0;
    let visible = false;
    let active = -1;
    let progress = 0;
    let movingTo = null;
    root.classList.add('is-ready');
    if (!pieces.length) return () => controller.abort();

    function select(index) {
      if (active === index) return;
      active = index;
      pieces.forEach((piece, i) => {
        piece.classList.toggle('is-active', i === index);
        piece.inert = !reduced.matches && i !== index;
        if (reduced.matches) piece.removeAttribute('aria-hidden');
        else piece.setAttribute('aria-hidden', String(i !== index));
      });
      buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      if (count) count.textContent = String(index + 1).padStart(2, '0');
      root.dataset.activePiece = pieces[index]?.dataset.name || '';
    }

    function render() {
      frame = 0;
      if (!visible && movingTo === null) return;
      root.classList.toggle('is-static', reduced.matches);
      root.classList.toggle('is-paused', isPaused());
      if (reduced.matches) {
        pieces.forEach(piece => {
          piece.style.removeProperty('opacity');
          piece.querySelector('.aw-exhibition-image').style.removeProperty('transform');
          piece.querySelector('.aw-exhibition-copy').style.removeProperty('transform');
          piece.inert = false;
          piece.removeAttribute('aria-hidden');
        });
        return;
      }
      const bounds = root.getBoundingClientRect();
      const travel = Math.max(1, bounds.height - stage.offsetHeight);
      progress = clamp(-bounds.top / travel);
      const step = progress * (pieces.length - 1);
      const index = Math.round(step);
      select(index);
      root.style.setProperty('--aw-exhibition-progress', progress.toFixed(4));
      pieces.forEach((piece, i) => {
        const distance = step - i;
        const proximity = clamp(1 - Math.abs(distance));
        const opacity = Number(i === index);
        piece.style.opacity = opacity.toFixed(3);
        const image = piece.querySelector('.aw-exhibition-image');
        const copy = piece.querySelector('.aw-exhibition-copy');
        const echo = piece.querySelector('.aw-exhibition-echo');
        if (isPaused()) {
          image.style.transform = 'none';
          copy.style.transform = 'none';
          echo.style.transform = 'none';
        } else {
          const offset = clamp(distance, -1, 1);
          const angle = (i % 2 ? 1 : -1) * (offset * 21);
          image.style.transform = `translate3d(${offset * -24}%,${Math.abs(offset) * 9}%,0) rotate(${angle}deg) scale(${1.04 - Math.abs(offset) * .2})`;
          copy.style.transform = `translate3d(0,${offset * -48}px,0)`;
          echo.style.transform = `translate3d(${offset * -10}%,0,0)`;
        }
      });
      if (movingTo !== null && Math.abs(progress - movingTo) < .006) movingTo = null;
    }

    function requestRender() {
      if (!frame) frame = requestAnimationFrame(render);
    }

    function goTo(index, moveFocus = false) {
      index = clamp(index, 0, pieces.length - 1);
      if (reduced.matches) {
        select(index);
        pieces[index].scrollIntoView({block: 'center', behavior: 'instant'});
        return;
      }
      const bounds = root.getBoundingClientRect();
      const travel = Math.max(1, bounds.height - stage.offsetHeight);
      movingTo = pieces.length > 1 ? index / (pieces.length - 1) : 0;
      window.scrollTo({top: window.scrollY + bounds.top + movingTo * travel, behavior: isPaused() ? 'instant' : 'smooth'});
      if (moveFocus) buttons[index].focus({preventScroll: true});
      requestRender();
    }

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => goTo(index), {signal});
      button.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % pieces.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + pieces.length) % pieces.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = pieces.length - 1;
        if (next !== undefined) { event.preventDefault(); goTo(next, true); }
      }, {signal});
    });
    const observer = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      if (visible) requestRender();
    }, {rootMargin: '120px'});
    observer.observe(root);
    const resize = new ResizeObserver(requestRender);
    resize.observe(root);
    window.addEventListener('scroll', requestRender, {passive: true, signal});
    window.addEventListener('resize', requestRender, {passive: true, signal});
    document.addEventListener('aw:motion-change', requestRender, {signal});
    reduced.addEventListener('change', () => { active = -1; visible = true; requestRender(); }, {signal});
    document.addEventListener('visibilitychange', () => { if (!document.hidden) requestRender(); }, {signal});
    root.addEventListener('aw:editor-select', event => {const index=pieces.indexOf(event.detail.block);if(index>=0)goTo(index);}, {signal});
    select(0);
    return () => { controller.abort(); observer.disconnect(); resize.disconnect(); cancelAnimationFrame(frame); };
  }

  function constellation(root) {
    const controller = new AbortController();
    const signal = controller.signal;
    const pieces = [...root.querySelectorAll('[data-aw-orbit-piece]')];
    const buttons = [...root.querySelectorAll('[data-aw-orbit-select]')];
    const tryLink = root.querySelector('[data-aw-orbit-try]');
    let active = -1;
    let inView = false;
    if (!pieces.length) return () => controller.abort();
    function syncMotion() {
      root.classList.toggle('is-paused', isPaused() || document.hidden);
      root.classList.toggle('is-in-view', inView && !document.hidden);
    }
    function select(index) {
      if (active === index) return;
      active = index;
      pieces.forEach((piece, i) => {
        piece.classList.toggle('is-active', i === index);
        piece.inert = i !== index;
        piece.setAttribute('aria-hidden', String(i !== index));
      });
      buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      if (tryLink && pieces[index]?.dataset.tryUrl) tryLink.href = pieces[index].dataset.tryUrl;
      root.dataset.activePiece = buttons[index]?.querySelector('span')?.textContent || '';
    }
    buttons.forEach((button, index) => {
      button.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch') {
          root.classList.add('is-interacting');
          select(index);
        }
      }, {signal});
      button.addEventListener('pointerleave', () => root.classList.remove('is-interacting'), {signal});
      button.addEventListener('focus', () => select(index), {signal});
      button.addEventListener('click', () => select(index), {signal});
      button.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % buttons.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = buttons.length - 1;
        if (next !== undefined) { event.preventDefault(); buttons[next].focus({preventScroll: true}); select(next); }
      }, {signal});
    });
    const observer = new IntersectionObserver(entries => { inView = entries[0].isIntersecting; syncMotion(); }, {threshold: .08});
    observer.observe(root);
    document.addEventListener('aw:motion-change', syncMotion, {signal});
    document.addEventListener('visibilitychange', syncMotion, {signal});
    reduced.addEventListener('change', syncMotion, {signal});
    root.addEventListener('aw:editor-select', event => {const index=pieces.indexOf(event.detail.block);if(index>=0)select(index);}, {signal});
    select(0);
    syncMotion();
    return () => { controller.abort(); observer.disconnect(); };
  }

  function mount(scope = document) {
    for (const [node, cleanup] of instances) if (!node.isConnected) { cleanup(); instances.delete(node); }
    scope.querySelectorAll('[data-aw-motion-exhibition],[data-aw-constellation]').forEach(root => {
      if (instances.has(root)) return;
      instances.set(root, root.hasAttribute('data-aw-motion-exhibition') ? exhibition(root) : constellation(root));
    });
  }
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, cleanup] of instances) if (event.target.contains(root)) { cleanup(); instances.delete(root); }
  });
  document.addEventListener('shopify:block:select', event => {
    const block = event.target.closest?.('[data-aw-editor-block]') || event.target.querySelector?.('[data-aw-editor-block]');
    if (!block) return;
    const root = block.closest('[data-aw-motion-exhibition],[data-aw-constellation]');
    if (root) root.dispatchEvent(new CustomEvent('aw:editor-select', {detail:{block}}));
  });
  document.addEventListener('shopify:section:reorder', () => requestAnimationFrame(() => mount()));
  window.awMotionGallery = {mount};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), {once: true});
  else mount();
})();
